import { useEffect, useState } from "react";
import { Home, User as UserIcon, TrendingUp, TrendingDown } from "lucide-react";
import api from "@/lib/api";
import { fmtMoney } from "@/lib/format";

/**
 * Compact dashboard widget that splits the current month's spending into
 * Personal vs Household buckets. Only rendered when the user has access to the
 * Family plan (since solo users have nothing to split).
 */
export default function HouseholdKPICard({ effectiveTier }) {
  const [data, setData] = useState(null);
  const eligible = effectiveTier === "family" || effectiveTier === "lifetime";

  useEffect(() => {
    if (!eligible) return;
    api.get("/stats/household-summary")
      .then(r => setData(r.data))
      .catch(() => {});
  }, [eligible]);

  if (!eligible || !data) return null;

  const totalExpense = data.total_expense || 0;
  const personalPct = totalExpense ? (data.personal.expense / totalExpense) * 100 : 0;
  const householdPct = totalExpense ? (data.household.expense / totalExpense) * 100 : 0;
  const base = data.base_currency;

  // Don't show if no spending recorded this month
  if (data.personal.count === 0 && data.household.count === 0) return null;

  return (
    <div className="card-surface p-5 sm:p-6 relative overflow-hidden" data-testid="household-kpi-card">
      <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-violet-500/10 blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-violet-300 font-bold">PLAN FAMILIA · ESTE MES</div>
            <div className="text-base sm:text-lg font-bold mt-0.5">Gasto personal vs hogar</div>
          </div>
          {totalExpense > 0 && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Total</div>
              <div className="text-base font-bold tabular-nums">{fmtMoney(totalExpense)} {base}</div>
            </div>
          )}
        </div>

        {/* Split bar */}
        {totalExpense > 0 && (
          <div className="h-2.5 rounded-full bg-white/5 overflow-hidden flex mb-4">
            <div className="bg-gradient-to-r from-sky-500 to-violet-500 transition-all" style={{ width: `${personalPct}%` }} />
            <div className="bg-gradient-to-r from-violet-500 to-emerald-500 transition-all" style={{ width: `${householdPct}%` }} />
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 min-w-0">
          <KPIBlock
            icon={UserIcon}
            label="Mis gastos"
            value={data.personal.expense}
            income={data.personal.income}
            count={data.personal.count}
            base={base}
            accent="sky"
            tid="kpi-personal"
          />
          <KPIBlock
            icon={Home}
            label="Hogar"
            value={data.household.expense}
            income={data.household.income}
            count={data.household.count}
            base={base}
            accent="violet"
            tid="kpi-household"
          />
        </div>
      </div>
    </div>
  );
}

function KPIBlock({ icon: Icon, label, value, income, count, base, accent, tid }) {
  const accents = {
    sky: { ring: "from-sky-500/15 to-sky-500/5 border-sky-500/30", text: "text-sky-300" },
    violet: { ring: "from-violet-500/15 to-violet-500/5 border-violet-500/30", text: "text-violet-300" },
  };
  const a = accents[accent];
  return (
    <div className={`p-3 sm:p-4 rounded-2xl bg-gradient-to-br ${a.ring} border min-w-0`} data-testid={tid}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className={`w-3.5 h-3.5 ${a.text} shrink-0`} />
        <div className={`text-[10px] uppercase tracking-wider font-bold ${a.text} truncate`}>{label}</div>
      </div>
      <div className="text-base sm:text-lg lg:text-xl font-bold tabular-nums text-white truncate">
        {fmtMoney(value)} <span className="text-xs text-slate-500 font-normal">{base}</span>
      </div>
      <div className="mt-1 flex items-center gap-3 text-[10px] text-slate-400 flex-wrap">
        {income > 0 && (
          <span className="flex items-center gap-0.5"><TrendingUp className="w-2.5 h-2.5 text-emerald-400 shrink-0" />{fmtMoney(income)}</span>
        )}
        <span className="flex items-center gap-0.5"><TrendingDown className="w-2.5 h-2.5 text-rose-400 shrink-0" />{count} {count === 1 ? "tx" : "txs"}</span>
      </div>
    </div>
  );
}
